import type { ApiErr } from '@cotera/client/app/etc/api/typed-client';
import {
  seedArtifactIsNew,
  type ChatSeedBuildInput,
  type ChatSeedPreSubmitContext,
  type ChatSeedPreSubmitResult,
  type ChatSeedSubmitParams,
} from '../../chat-seed/types';
import { DEFAULT_NEW_WORKFLOW_NAME } from '../workflow-draft-graph';
import { getWorkflowSeedRuntime } from './runtime';

const WORKFLOW_ARTIFACT_TYPE = 'workflow';

function resolveWorkflowSeedInputFromSubmit(
  params: ChatSeedSubmitParams
): ChatSeedBuildInput | null {
  const artifact = params.initialArtifacts?.find(
    (entry) => entry.artifactType === WORKFLOW_ARTIFACT_TYPE
  );
  if (artifact === undefined || artifact.artifactId === undefined) {
    return null;
  }

  const data = artifact.data as { isNew?: boolean } | null | undefined;

  return {
    artifactId: artifact.artifactId,
    isNew: data?.isNew === true,
  };
}

function isWorkflowAlreadyExistsError(error: ApiErr): boolean {
  if (error.t !== 'application') {
    return false;
  }
  const message = error.err.message ?? '';
  return message.includes('Workflow already exists');
}

async function createWorkflowOnServer(
  ctx: ChatSeedPreSubmitContext,
  input: ChatSeedBuildInput
): Promise<ChatSeedPreSubmitResult> {
  const createResult = await ctx.clients.workflow.create({
    id: input.artifactId,
    name: DEFAULT_NEW_WORKFLOW_NAME,
    ...(ctx.folderId !== undefined && { folderId: ctx.folderId }),
  });

  if (createResult.isErr()) {
    if (isWorkflowAlreadyExistsError(createResult.error)) {
      return { ok: true };
    }
    const message =
      createResult.error.t === 'application'
        ? createResult.error.err.message ?? 'Failed to create workflow'
        : 'Failed to create workflow';
    return { ok: false, message };
  }

  return { ok: true };
}

async function persistNewWorkflow(
  ctx: ChatSeedPreSubmitContext,
  input: ChatSeedBuildInput
): Promise<ChatSeedPreSubmitResult> {
  const runtime = getWorkflowSeedRuntime(input.artifactId);
  if (runtime !== undefined) {
    if (runtime.hasDraftChangesAgainstBaseline?.() === true) {
      return {
        ok: false,
        message: 'Save the workflow before sending a message',
      };
    }
    if (runtime.isNew()) {
      if (runtime.persistNewWorkflow !== undefined) {
        return await runtime.persistNewWorkflow({
          ...(ctx.folderId !== undefined && { folderId: ctx.folderId }),
        });
      }
      const result = await createWorkflowOnServer(ctx, input);
      if (result.ok) {
        runtime.markPersisted();
      }
      return result;
    }
    if (runtime.hasUnsavedChanges()) {
      return {
        ok: false,
        message: 'Save the workflow before sending a message',
      };
    }
    return { ok: true };
  }

  return createWorkflowOnServer(ctx, input);
}

export async function workflowChatSeedPreSubmit(
  ctx: ChatSeedPreSubmitContext
): Promise<ChatSeedPreSubmitResult | null> {
  const input = resolveWorkflowSeedInputFromSubmit(ctx.params);
  if (
    input === null ||
    !seedArtifactIsNew(ctx.params, WORKFLOW_ARTIFACT_TYPE, input.artifactId)
  ) {
    return null;
  }

  return persistNewWorkflow(ctx, input);
}
